/**
 * RiderSettlementHistory Component
 * 라이더 주정산내역 페이지 (중개사/협력사/본사용)
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import {useRiderSettlementData} from '../hooks/useRiderSettlementData';
import {useInitialLoadingState} from '../../../shared/hooks/useInitialLoadingState';
import {useUserInfo} from '@/shared/hooks/useCrudOperations';
import {usePagination} from '@/shared/hooks/usePagination';
import {useColumnSort} from '@/shared/hooks/useTableSort';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import Loading from '@/shared/components/feedback/Loading';
import {loadFilters, saveFilters} from '@/shared/utils/filterStorage';
import {
    getInitialDateRange,
    validateFilters,
    getSettlementValueByIndex,
} from '../utils/riderSettlementHelpers';
import { FilterState, RiderSettlement } from '../types/riderSettlement.types';
import FilterSection from './FilterSection';
import SettlementTable from './SettlementTable';

import '@/shared/styles/common-table.css';

/** 필터 저장 키 */
const FILTER_STORAGE_KEY = 'riderSettlementFilters';

/** 페이지당 아이템 수 */
const ITEMS_PER_PAGE = 10;

const RiderSettlementHistory: React.FC = () => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    // ======== 사용자 정보 ========
    const userInfo = getUserInfo();
    const userType = userInfo?.userType;
    const isHeadquartersUser = userType === 'HEADQUARTERS';
    const isSupplierUser = userType === 'SUPPLIER';
    const isRiderUser = userType === 'RIDER';
    const userSupplierId = isSupplierUser ? userInfo?.userId : null;
    const userRiderId = isRiderUser ? userInfo?.userId : null;

    // 저장된 필터가 있으면 초기 자동 조회 건너뛰기
    const skipInitialFetch = useMemo(() => {
        const saved = loadFilters<FilterState>(FILTER_STORAGE_KEY);
        return !!(saved?.acctStDate && saved?.acctEdDate);
    }, []);

    // ======== 데이터 훅 ========
    const {
        settlements,
        suppliers,
        latestStartDate,
        latestEndDate,
        loading,
        refreshSettlements,
        downloadExcel
    } = useRiderSettlementData(userSupplierId, userRiderId, skipInitialFetch);

    const isInitialLoading = useInitialLoadingState(loading);

    // ======== 필터 상태 ========
    const [filters, setFilters] = useState<FilterState>(() => {
        const initialSupplierId = isSupplierUser ? (userSupplierId || '') : '';
        const saved = loadFilters<FilterState>(FILTER_STORAGE_KEY);
        return saved ? {
            acctStDate: saved.acctStDate || '',
            acctEdDate: saved.acctEdDate || '',
            riderName: saved.riderName || '',
            supplierId: initialSupplierId
        } : {
            acctStDate: '',
            acctEdDate: '',
            riderName: '',
            supplierId: initialSupplierId
        };
    });

    // ======== 정렬 ========
    const {
        sortConfig,
        sortedData: sortedSettlements,
        handleSort,
        resetSort
    } = useColumnSort<RiderSettlement>({
        data: settlements,
        getValueByIndex: getSettlementValueByIndex
    });

    // ======== 페이지네이션 ========
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: sortedSettlements, itemsPerPage: ITEMS_PER_PAGE });

    const hasAppliedInitialFilter = useRef(false);

    // ======== Effects ========

    useEffect(() => {
        const filtersToSave = {
            acctStDate: filters.acctStDate,
            acctEdDate: filters.acctEdDate,
            riderName: filters.riderName,
            supplierId: isSupplierUser ? filters.supplierId : ''
        };
        saveFilters(FILTER_STORAGE_KEY, filtersToSave);
    }, [filters, isSupplierUser]);

    useEffect(() => {
        if (hasAppliedInitialFilter.current) return;
        if (!latestStartDate || !latestEndDate || loading) return;

        if (!filters.acctStDate || !filters.acctEdDate) {
            const { startDate, endDate } = getInitialDateRange(latestStartDate, latestEndDate);
            const initialSupplierId = isSupplierUser ? (userSupplierId || '') : '';
            setFilters(prev => ({
                ...prev,
                acctStDate: startDate,
                acctEdDate: endDate,
                supplierId: initialSupplierId
            }));
        }

        if (skipInitialFetch) {
            const saved = loadFilters<FilterState>(FILTER_STORAGE_KEY);
            if (saved?.acctStDate && saved?.acctEdDate) {
                refreshSettlements(
                    saved.acctStDate,
                    saved.acctEdDate,
                    saved.riderName || undefined,
                    saved.supplierId || undefined
                );
            }
        }

        hasAppliedInitialFilter.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestStartDate, latestEndDate, loading, skipInitialFetch]);

    // ======== Handlers ========

    const handleFilterChange = useCallback((key: string, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleSearch = useCallback(async () => {
        const validation = validateFilters(
            filters.acctStDate,
            filters.acctEdDate,
            filters.supplierId,
            isRiderUser
        );
        if (!validation.isValid) {
            showToast(validation.message!, { type: ToastType.WARNING });
            return;
        }

        const resultCount = await refreshSettlements(
            filters.acctStDate,
            filters.acctEdDate,
            filters.riderName || undefined,
            filters.supplierId || undefined
        );
        resetPagination();

        if (resultCount === 0) {
            showToast('조회된 정산내역이 없습니다.', { type: ToastType.INFO });
        }
    }, [filters, isRiderUser, refreshSettlements, resetPagination, showToast]);

    const handleReset = useCallback(() => {
        const initialSupplierId = isSupplierUser ? (userSupplierId || '') : '';

        setFilters({
            acctStDate: latestStartDate || '',
            acctEdDate: latestEndDate || '',
            riderName: '',
            supplierId: initialSupplierId
        });

        resetSort();
        resetPagination();

        if (latestStartDate && latestEndDate && (isRiderUser || initialSupplierId || !isSupplierUser)) {
            refreshSettlements(
                latestStartDate,
                latestEndDate,
                undefined,
                initialSupplierId || undefined
            );
        }
    }, [isSupplierUser, userSupplierId, latestStartDate, latestEndDate, isRiderUser, resetPagination, refreshSettlements, resetSort]);

    const handleExcelDownload = useCallback(async () => {
        if (loading) return;

        const validation = validateFilters(
            filters.acctStDate,
            filters.acctEdDate,
            filters.supplierId,
            isRiderUser
        );
        if (!validation.isValid) {
            showToast(validation.message!, { type: ToastType.WARNING });
            return;
        }

        await downloadExcel(
            filters.acctStDate,
            filters.acctEdDate,
            filters.riderName || undefined,
            filters.supplierId || undefined
        );
    }, [filters, isRiderUser, loading, downloadExcel, showToast]);

    // ======== Render ========
    if (loading && !isInitialLoading) {
        return <Loading message="엑셀 다운로드 중..." />;
    }

    if (isInitialLoading) {
        return <Loading message="라이더 주정산내역을 불러오는 중..." />;
    }

    return (
        <div className="supplier-selection-page">
            <div className="page-title-wrapper">
                <h1 className="page-title">라이더 주정산내역</h1>
            </div>

            <FilterSection
                filters={filters}
                suppliers={suppliers}
                isRiderUser={isRiderUser}
                isSupplierUser={isSupplierUser}
                isHeadquartersUser={isHeadquartersUser}
                downloading={loading}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onReset={handleReset}
                onExcelDownload={handleExcelDownload}
            />

            <SettlementTable
                settlements={sortedSettlements}
                currentItems={currentItems}
                currentPage={currentPage}
                loading={loading}
                isRiderUser={isRiderUser}
                sortConfig={sortConfig}
                onSort={handleSort}
            />

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default RiderSettlementHistory;
